import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  IconButton,
  Text,
} from '@chakra-ui/react';
import { ReactComponent as SettingsIcon } from 'assets/settings-icon.svg';
import { useContext, useMemo } from 'react';
import { useMediaQuery } from 'react-responsive';
import { format, isToday, isYesterday } from 'date-fns';
import { GlobalContext } from 'providers/Global';
import { NotificationContext } from 'providers/Notification';
import { ModalType, SCREEN_MD } from '../../utils/constants';
import NotificationItem from '../NotificationItem';
import { Notification } from '../../types/notification';

interface TimeGroup {
  label: string;
  notifications: Notification[];
}

const groupByTime = (notifications: Notification[]): TimeGroup[] => {
  const groups = new Map<string, Notification[]>();

  for (const notification of notifications) {
    const date = new Date(notification.createdAt);
    let label: string;

    if (isToday(date)) {
      label = 'Today';
    } else if (isYesterday(date)) {
      label = 'Yesterday';
    } else {
      label = format(date, 'MMMM d, yyyy');
    }

    const existing = groups.get(label);
    if (existing) {
      existing.push(notification);
    } else {
      groups.set(label, [notification]);
    }
  }

  return Array.from(groups.entries()).map(([label, items]) => ({
    label,
    notifications: items,
  }));
};

const NotificationDrawer = () => {
  const {
    modals: {
      [`${ModalType.NotificationDrawer}`]: { open },
    },
    closeModal,
    openModal,
  } = useContext(GlobalContext);

  const {
    notifications,
    dismissNotification,
    dismissAll,
    navigateToNotification,
  } = useContext(NotificationContext);

  const isMobile = useMediaQuery({ query: `(max-width: ${SCREEN_MD})` });

  const timeGroups = useMemo(
    () => groupByTime(notifications),
    [notifications],
  );

  const handleNavigate = (notification: Notification) => {
    closeModal(ModalType.NotificationDrawer);
    navigateToNotification(notification);
  };

  return (
    <Drawer
      isOpen={open}
      onClose={() => closeModal(ModalType.NotificationDrawer)}
      placement="right"
      size={isMobile ? 'full' : 'lg'}
    >
      <DrawerOverlay backdropFilter="blur(10px)" />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader borderBottomWidth="1px">
          <Flex justifyContent="space-between" alignItems="center" pr={8}>
            <Flex alignItems="center" gap={2}>
              <Text variant="bodyBold" fontSize="xl">
                Notifications
              </Text>
              <IconButton
                aria-label="Notification settings"
                icon={<SettingsIcon style={{ width: 18, height: 18 }} />}
                variant="ghost"
                size="sm"
                borderRadius="full"
                onClick={() => {
                  closeModal(ModalType.NotificationDrawer);
                  openModal(ModalType.NotificationSettingsDrawer);
                }}
              />
            </Flex>
            {notifications.length >= 2 && (
              <Button
                size="xs"
                variant="ghost"
                colorScheme="red"
                onClick={() => dismissAll()}
              >
                Dismiss All
              </Button>
            )}
          </Flex>
        </DrawerHeader>
        <DrawerBody p={0}>
          {notifications.length === 0 ? (
            <Flex py={16} justifyContent="center" alignItems="center">
              <Text color="blackAlpha.500" fontSize="sm">
                No notifications
              </Text>
            </Flex>
          ) : (
            timeGroups.map((group) => (
              <Box key={group.label}>
                <Box px={4} py={2} bg="primary">
                  <Text
                    fontSize="xs"
                    color="blackAlpha.500"
                    textTransform="uppercase"
                    fontWeight={600}
                    letterSpacing="0.05em"
                  >
                    {group.label}
                  </Text>
                </Box>
                {group.notifications.map((notification) => (
                  <NotificationItem
                    key={notification._id}
                    notification={notification}
                    onDismiss={dismissNotification}
                    onNavigate={handleNavigate}
                  />
                ))}
              </Box>
            ))
          )}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};

export default NotificationDrawer;
